import {
  Activity,
  AlertTriangle,
  Archive,
  ArrowLeftRight,
  BadgeCheck,
  BarChart3,
  Bell,
  Bot,
  Boxes,
  Brain,
  BrainCircuit,
  Building2,
  Calendar,
  ChartPie,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  Cog,
  Compass,
  Contact,
  Database,
  Download,
  FileAudio,
  FileClock,
  FileText,
  Flag,
  FlaskConical,
  Gauge,
  GitBranch,
  Grid3x3,
  Headphones,
  History,
  Inbox,
  KeyRound,
  LayoutDashboard,
  Library,
  LineChart,
  ListChecks,
  ListTodo,
  LogIn,
  Mail,
  MessageSquare,
  MessageSquareText,
  Mic,
  Network,
  NotebookPen,
  Phone,
  PhoneCall,
  PhoneMissed,
  PieChart,
  Radio,
  RotateCcw,
  ScrollText,
  SearchCheck,
  Settings,
  Shield,
  ShieldAlert,
  Sliders,
  Sparkles,
  Star,
  Tag,
  Target,
  Timer,
  Users,
  Voicemail,
  Webhook,
  Wrench,
  Zap,
  type LucideIcon,
} from 'lucide-react';

/**
 * Unfold's sidebar config names Material Symbols icons. The bootstrap payload
 * passes those names through, so the console maps them onto the closest Lucide
 * equivalent here rather than pulling in a second icon font.
 */
const ICON_MAP: Record<string, LucideIcon> = {
  dashboard: LayoutDashboard,
  space_dashboard: LayoutDashboard,
  monitoring: Activity,
  insights: BarChart3,
  analytics: BarChart3,
  bar_chart: BarChart3,
  pie_chart: PieChart,
  donut_large: ChartPie,
  speed: Gauge,
  timer: Timer,
  history: History,
  schedule: Calendar,
  calendar_month: Calendar,

  call: Phone,
  phone_in_talk: PhoneCall,
  phone: Phone,
  phone_missed: PhoneMissed,
  support_agent: Headphones,
  headset_mic: Headphones,
  voicemail: Voicemail,
  mic: Mic,
  graphic_eq: Radio,
  audio_file: FileAudio,
  record_voice_over: Mic,

  group: Users,
  groups: Users,
  person: Users,
  contacts: Contact,
  contact_phone: Contact,
  badge: KeyRound,
  key: KeyRound,
  shield: Shield,
  admin_panel_settings: Shield,
  gpp_maybe: ShieldAlert,
  report: Flag,
  flag: Flag,
  warning: AlertTriangle,
  gavel: ShieldAlert,

  business: Building2,
  corporate_fare: Building2,
  account_tree: Network,
  hub: Network,
  alt_route: GitBranch,
  route: GitBranch,
  device_hub: Network,

  settings: Settings,
  tune: Sliders,
  build: Wrench,
  manufacturing: Cog,
  smart_toy: Bot,
  psychology: Brain,
  auto_awesome: Sparkles,
  memory: Brain,
  neurology: Brain,

  inbox: Inbox,
  mark_email_unread: Mail,
  mail: Mail,
  forum: MessageSquare,
  chat: MessageSquare,
  notifications: Bell,
  task_alt: CheckCircle2,
  checklist: ListChecks,
  assignment: ClipboardList,
  fact_check: ClipboardList,
  description: FileText,
  article: ScrollText,
  receipt_long: ScrollText,

  storage: Database,
  database: Database,
  folder: Archive,
  inventory_2: Boxes,
  category: Tag,
  label: Tag,
  local_offer: Tag,
  star: Star,
  target: Target,
  library_books: Library,

  // Filled in for the icon-only rail: an unmapped name renders the generic
  // Boxes cube, which is indistinguishable at 16px.
  people: Users,
  grid_on: Grid3x3,
  rule: ListTodo,
  bolt: Zap,
  query_stats: LineChart,
  verified: BadgeCheck,
  rate_review: MessageSquareText,
  psychology_alt: BrainCircuit,
  edit_note: NotebookPen,
  travel_explore: Compass,
  replay: RotateCcw,
  science: FlaskConical,
  assignment_turned_in: ClipboardCheck,
  webhook: Webhook,
  manage_history: FileClock,
  download: Download,
  swap_calls: ArrowLeftRight,
  saved_search: SearchCheck,
  login: LogIn,
};

const warnedIcons = new Set<string>();

// The repo's tsconfig does not load vite/client types, so read the flag loosely.
const IS_DEV = Boolean((import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV);

export function NavIcon({ name, className }: { name: string; className?: string }) {
  const Icon = ICON_MAP[name] ?? Boxes;
  if (IS_DEV && name && !(name in ICON_MAP) && !warnedIcons.has(name)) {
    warnedIcons.add(name);
    // Surface new backend icon names immediately instead of silently rendering
    // the generic cube in an icon-only rail.
    console.warn(`NavIcon: no Lucide mapping for "${name}" — add it to ICON_MAP in src/lib/icons.tsx`);
  }
  return <Icon className={className} aria-hidden />;
}

export function hasIcon(name: string): boolean {
  return name in ICON_MAP;
}
